import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { getHighestVisitedStep, getKmats } from "~/features/configurator/Linear/redux/selectors";
import { sendCarriageUpdate } from "~/features/configurator/Carriage/api/sendCarriageConfiguratorUpdate";
import {
  sendCarriageMiscChange,
  sendCarriageStepChange,
} from "~/features/configurator/Carriage/api/sendCarriageStepChange";
import { sendCarriageDataReset } from "~/features/configurator/Carriage/api/sendCarriageStepReset";
import { sendCarriageResetConfigurator } from "~/features/configurator/Carriage/api/sendCarriageResetConfigurator";
import { sendCarriageAddToCart } from "~/features/configurator/Carriage/api/sendCarriageAddToCart";
import { CarriageSummaryData } from "~/features/configurator/Carriage/v2/configuration/classic/summary/CarriageSummaryData";
import { type RootState } from "~/app/store";
import {
  CARRIAGE_LOCATING_CSTIC_NAME,
  CARRIAGE_MIN_STEP_TO_BUY,
  CARRIAGE_MIN_STEP_TO_SAVE,
  FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES,
  FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE,
} from "~/features/configurator/Carriage/carriageConfiguratorConstants";
import { type LinearCsticValue, type SubstepTile } from "~/features/configurator/Linear/models";
import {
  BASE_DESIGNATION_SEPARATOR,
  CENTRAL_CONNECTORS,
  CENTRAL_CONNECTORS_LABEL,
  CENTRAL_CONNECTORS_VALUE,
  CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME,
  CSTIC_CARRIAGE_LEFT_NAME,
  CSTIC_CARRIAGE_MIDDLE_NAME,
  CSTIC_CARRIAGE_RIGHT_NAME,
  CSTIC_DESIGN_TYPE_SUBLEVEL_NAME,
  CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  CSTIC_WAGENBAUFORM_NAME,
  MANUAL_CONNECTORS,
  MANUAL_CONNECTORS_LABEL,
  MANUAL_CONNECTORS_VALUE,
} from "~/features/configurator/Linear/constants";
import { trimCollapseWhitespace } from "~/features/configurator/Linear/utils";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import { grabXmlTranslation } from "~/features/configurator/grabExternalTranslation";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import * as CarriageCstic from "~/features/configurator/Carriage/v2/configuration/CarriageCstics";
import { pushStandardValueIfMissing } from "~/features/configurator/common/v2/helper";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import { addFakeCstic } from "~/features/configurator/transformerHelper";
import { CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { SubstepType } from "../Linear/dtos";
import { prepareSubStepsInfo, transformSummaryDataToV2, transformToSelectionData } from "../Shaft/SummaryDataProvider";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep3SummaryData,
} from "./v2/configuration/classic/summary/CarriageSummaryDataProvider";
import { CsticNamingHelper } from "../Linear/v2/configuration/kllt/CsticNamingHelper";

const CARRIAGE_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.Series, SubstepType.Size];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = CARRIAGE_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },

  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = CARRIAGE_CONFIGURATOR_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step3Data = getStep3SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(3),
        gaName: summaryDataProvider.getGaStepName(3),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step3Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = CARRIAGE_CONFIGURATOR_STEP_DATA[2].prepareData(state, "en");
      const selectionDataAlt = transformToSelectionData(tables);

      const summaryDataProvider = makeSummaryDataProvider(state);
      const substeps = state.linearConfigurator.substeps;
      const kmats = getKmats(state);
      const carriageKmat = getMainKmat({ kmats });

      const lubricationSubstep = substeps.Lubrication;
      const lubricationTiles: SubstepTile[] = [...lubricationSubstep.tiles];
      lubricationSubstep.groupedTiles.forEach((group) => lubricationTiles.push(...group.tiles));
      const lubricationSelectedTile = lubricationTiles[lubricationSubstep.selectedTile!];

      const smdsTile =
        substeps.SMDS.isVisible && substeps.SMDS.selectedTile !== null
          ? substeps.SMDS.tiles[substeps.SMDS.selectedTile!]
          : null;
      const smdeTile =
        substeps.SMDE.isVisible && substeps.SMDE.selectedTile !== null
          ? substeps.SMDE.tiles[substeps.SMDE.selectedTile!]
          : null;

      const locatingFaceSubstep = substeps.LocatingFace;
      const locatingFaceCstic = carriageKmat.cstics[CARRIAGE_LOCATING_CSTIC_NAME];
      const locatingFaceTile = locatingFaceCstic.values.find((val) => val.value === locatingFaceCstic.value);

      const singleCarriageReference = carriageKmat.cstics[CSTIC_WAGENBAUFORM_NAME];
      const carriageRefSelectedTile = singleCarriageReference.values.find(
        (singleValue) => singleValue.value === singleCarriageReference.value,
      );

      const kitLeft = carriageKmat.cstics[CSTIC_CARRIAGE_LEFT_NAME];
      const kitMid = carriageKmat.cstics[CSTIC_CARRIAGE_MIDDLE_NAME];
      const kitRight = carriageKmat.cstics[CSTIC_CARRIAGE_RIGHT_NAME];

      const lubricationConnectorCstic = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_NAME];
      const lubricationConnectorSelectedTile = lubricationConnectorCstic?.values.find(
        (singleValue) => singleValue.key === lubricationConnectorCstic.value,
      );

      const lubricationPosition = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_POSITION_NAME];
      const lubricationPositionSelectedTile = lubricationPosition?.values.find(
        (singleValue) => singleValue.key === lubricationPosition.value,
      );

      const lubricationPortSelection = carriageKmat.cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME];
      const lubricationPortSelectionSelectedTile = lubricationPortSelection?.values.find(
        (singleValue) => singleValue.key === lubricationPortSelection.value,
      );

      const lubricationDegree = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME];
      const lubricationDegreeSelectedTile = lubricationDegree?.values.find(
        (singleValue) => singleValue.key === lubricationDegree.value,
      );

      const SMDE = grabXmlTranslation(summaryDataProvider, lubricationSelectedTile.name ?? "-");
      const lubrification = grabXmlTranslation(summaryDataProvider, lubricationConnectorSelectedTile?.label ?? "-");
      const SMDS = grabXmlTranslation(summaryDataProvider, smdsTile?.name ?? "-");
      const locating_face = grabXmlTranslation(
        summaryDataProvider,
        locatingFaceTile?.label ?? locatingFaceCstic.value ?? "-",
      );
      const carriage_type = grabXmlTranslation(summaryDataProvider, carriageRefSelectedTile?.label ?? "-");
      const kit_left = grabXmlTranslation(summaryDataProvider, kitLeft?.value ?? "-");
      const kit_centre = grabXmlTranslation(summaryDataProvider, kitMid?.value ?? "-");
      const kit_right = grabXmlTranslation(summaryDataProvider, kitRight?.value ?? "-");
      const lubrification_connector = grabXmlTranslation(
        summaryDataProvider,
        lubricationConnectorSelectedTile?.label ?? "-",
      );
      const lubrification_port_alignment = grabXmlTranslation(
        summaryDataProvider,
        lubricationPositionSelectedTile?.label ?? "-",
      );
      const lubrification_port_selection = grabXmlTranslation(
        summaryDataProvider,
        lubricationPortSelectionSelectedTile?.label ?? "-",
      );
      const lubrification_port_degree = grabXmlTranslation(
        summaryDataProvider,
        lubricationDegreeSelectedTile?.label ?? "-",
      );

      const selectionData = {
        lubrification: trimCollapseWhitespace(lubrification),
        SMDS: trimCollapseWhitespace(SMDS),
        SMDE: trimCollapseWhitespace(SMDE),
        locating_face: trimCollapseWhitespace(locating_face),
        carriage_type: trimCollapseWhitespace(carriage_type),
        kit_left: trimCollapseWhitespace(kit_left),
        kit_centre: trimCollapseWhitespace(kit_centre),
        kit_right: trimCollapseWhitespace(kit_right),
        lubrification_connector: trimCollapseWhitespace(lubrification_connector),
        lubrification_port_alignment: trimCollapseWhitespace(lubrification_port_alignment),
        lubrification_port_selection: trimCollapseWhitespace(lubrification_port_selection),
        lubrification_port_degree: trimCollapseWhitespace(lubrification_port_degree),
      };
      sendStepCompletedEvent(summaryDataProvider.getGaStepName(3), selectionData);
    },
  },
];

export const CARRIAGE_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Carriage,
  productCode: CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.carriage.name",
  },
  apiCalls: {
    update: sendCarriageUpdate,
    reset: sendCarriageResetConfigurator,
    stepChange: sendCarriageStepChange,
    stepReset: sendCarriageDataReset,
    miscChange: sendCarriageMiscChange,
    addToCart: sendCarriageAddToCart,
  },
  summary: CarriageSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= CARRIAGE_MIN_STEP_TO_BUY;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= CARRIAGE_MIN_STEP_TO_SAVE;
  },
  stepHardcodedData: CARRIAGE_CONFIGURATOR_STEP_DATA,
  generateDesignation: (kmats) => {
    /*
    According to bug SCHAEFF-17623, if VVER_065 suffix is "/" or null, do not include it in the base designation
   */
    const kmat = getMainKmat({ kmats });

    let designType = kmat.cstics[CSTIC_DESIGN_TYPE_SUBLEVEL_NAME]?.value;
    const size = kmat.cstics["VBGR_065"]?.value ?? "";
    let suffix = kmat.cstics["VVER_065"]?.value?.toUpperCase();

    if (!designType) {
      return BASE_DESIGNATION_SEPARATOR;
    }

    if (designType?.includes("..-")) {
      [designType, suffix] = designType.split("..-");
    }

    return [designType + size, suffix]
      .filter(Boolean)
      .filter((item) => item !== "/")
      .join(BASE_DESIGNATION_SEPARATOR);
  },
  gaConfiguratorName: "carriage",
  modelAndUpdatePreprocessor: (model, dto) => {
    const kmat = getMainKmat({ kmats: model.kmats });

    const namingHelper = new CsticNamingHelper(kmat.cstics);
    const cstics = kmat.cstics;

    pushStandardValueIfMissing(kmat, CarriageCstic.GreasingPerGreaseGroup, "/");
    pushStandardValueIfMissing(kmat, CarriageCstic.GreasingLType, "/");

    const locatingFaceCstic = cstics[CarriageCstic.LocatingFace];
    if (locatingFaceCstic) {
      locatingFaceCstic.values?.forEach((val) => {
        val.label = `configurator.carriage.cstic.locatingFace.${val.value}.name`;
      });
    }

    let designSuffix = "";
    const designValue = cstics[CarriageCstic.MaterialName]?.value;
    if (designValue === "RWU") {
      designSuffix = ".RWU";
    } else if (designValue === "KWSE") {
      designSuffix = ".KWSE";
    } else if (designValue === "KWVE") {
      designSuffix = ".KWVE";
    } else if (designValue === "KWVE..-W") {
      designSuffix = ".KWVE-W";
    }

    namingHelper.updateNames(CarriageCstic.KitsLeft, {
      label: "configurator.carriage.cstic.kit.left.name",
      info: "configurator.carriage.cstic.kit.left.info" + designSuffix,
    });

    namingHelper.updateNames(CarriageCstic.KitsCenter, {
      label: "configurator.carriage.cstic.kit.center.name",
      info: "configurator.carriage.cstic.kit.center.info" + designSuffix,
    });

    namingHelper.updateNames(CarriageCstic.KitsRight, {
      label: "configurator.carriage.cstic.kit.right.name",
      // "left" on purpose
      info: "configurator.carriage.cstic.kit.left.info" + designSuffix,
    });

    namingHelper.updateNames(CarriageCstic.LubricationConnectorOrientation, {
      label: "configurator.carriage.cstic.lubricationConnectorStatus.name",
      value: {
        label: (value) => `configurator.carriage.cstic.lubricationConnectorStatus.${value}.name`,
      },
    });

    namingHelper.updateNames(CarriageCstic.LubricationConnector, {
      label: "configurator.carriage.cstic.lubricationConnector.name",
      value: {
        label: (value) => value,
      },
    });

    namingHelper.updateNames(CarriageCstic.LubricationConnectorPosition, {
      label: "configurator.carriage.cstic.lubricationConnectorOrientation.name",
      info: "configurator.carriage.cstic.lubricationConnectorOrientation.info",
      value: {
        label: (value) => `configurator.carriage.cstic.lubricationConnectorOrientation.${value}.name`,
      },
    });

    namingHelper.updateNames(CarriageCstic.LubricationConnectorAlignment, {
      label: "configurator.carriage.cstic.angularOrientation.name",
      info: "configurator.carriage.cstic.angularOrientation.info",
      value: {
        label: (value) => `configurator.carriage.cstic.angularOrientation.${value.replace("°", "")}.name`,
      },
    });

    const lubricationConnectorCstic = cstics[CarriageCstic.LubricationConnector];
    if (lubricationConnectorCstic?.values) {
      let connectorType;
      if (!kmat.cstics[FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE]) {
        connectorType = determineConnectorType(lubricationConnectorCstic.value ?? "") ?? MANUAL_CONNECTORS_VALUE;
        addFakeCstic(kmat, {
          name: FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE,
          value: connectorType,
          values: [
            {
              value: MANUAL_CONNECTORS_VALUE,
              label: MANUAL_CONNECTORS_LABEL,
              key: MANUAL_CONNECTORS_VALUE,
              info: "",
              imageUrl: "",
            },
            {
              value: CENTRAL_CONNECTORS_VALUE,
              label: CENTRAL_CONNECTORS_LABEL,
              key: CENTRAL_CONNECTORS_VALUE,
              info: "",
              imageUrl: "",
            },
          ],
        });
      } else {
        connectorType = kmat.cstics[FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE]?.value ?? MANUAL_CONNECTORS_VALUE;
      }
      lubricationConnectorCstic.values = filterByConnectorType(lubricationConnectorCstic.values, connectorType);
    }

    addFakeCstic(
      model,
      FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES,
      dto.enableConfigureCarriageValues ? "true" : "false",
    );

    return model;
  },
} as const;

const sendStepCompletedEvent = makeSendStepCompletedEvent(CARRIAGE_DEFINITION);

function determineConnectorType(value: string) {
  if (MANUAL_CONNECTORS.includes(value)) {
    return MANUAL_CONNECTORS_VALUE;
  }
  if (CENTRAL_CONNECTORS.includes(value)) {
    return CENTRAL_CONNECTORS_VALUE;
  }
  return null;
}
function filterByConnectorType(values: LinearCsticValue[], connectorType: string): LinearCsticValue[] {
  if (!connectorType) {
    throw new Error("Connector type is undefined");
  }
  const validValues = new Set(connectorType === MANUAL_CONNECTORS_VALUE ? MANUAL_CONNECTORS : CENTRAL_CONNECTORS);
  return values.filter((value) => validValues.has(value.value));
}
